import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Badge, Button, Card, DataTable, type Column } from '@/components/ui';
import { useSessionStaff } from '@/features/staff-sessions/api';
import { StaffSessionsDrawer } from '@/features/staff-sessions/StaffSessionsDrawer';
import type { SessionStaff } from '@/features/staff-sessions/types';
import { PageContainer } from '@/layouts/PageContainer';
import styles from './StaffSessionsPage.module.css';

export function StaffSessionsPage() {
  const { t } = useTranslation();
  const staff = useSessionStaff();
  const [selected, setSelected] = useState<SessionStaff | null>(null);
  const columns: Column<SessionStaff>[] = [
    {
      key: 'staff',
      header: t('staffSessions.staffMember'),
      render: (row) => (
        <>
          <p className={styles.name}>{row.fullName}</p>
          <p className={styles.status}>{row.status}</p>
        </>
      ),
    },
    {
      key: 'status',
      header: t('staff.status'),
      render: (row) => (
        <Badge tone={row.status === 'active' ? 'success' : 'neutral'} dot>
          {row.status}
        </Badge>
      ),
    },
    {
      key: 'actions',
      header: (
        <span className="visually-hidden">{t('staffSessions.actions')}</span>
      ),
      align: 'right',
      render: (row) => (
        <Button size="sm" onClick={() => setSelected(row)}>
          {t('staffSessions.viewSessions')}
        </Button>
      ),
    },
  ];

  return (
    <PageContainer>
      <Card title={t('staffSessions.pageTitle')} flush>
        <DataTable
          caption={t('staffSessions.staffDirectory')}
          rows={staff.data}
          rowKey={(row) => row.id}
          columns={columns}
          loading={staff.isLoading}
          error={staff.error}
          onRetry={() => void staff.refetch()}
        />
      </Card>
      {selected && (
        <StaffSessionsDrawer
          staffId={selected.id}
          staffName={selected.fullName}
          onClose={() => setSelected(null)}
        />
      )}
    </PageContainer>
  );
}
